import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { InspectionsFindingDto } from '../types/dtos/inspection';
import { inspectionFindingService } from '../services/inspection-finding.service';

const FINDINGS_KEY = 'inspection-findings';

export const useInspectionFindings = (
  organizationId: string,
  inspectionId: string,
  isActive?: boolean
) => {
  return useQuery({
    queryKey: [FINDINGS_KEY, organizationId, inspectionId, { isActive }],
    queryFn: () =>
      inspectionFindingService
        .findAll(organizationId, inspectionId, isActive)
        .then((res) => res.data),
    enabled: !!organizationId && !!inspectionId,
  });
};

export const useInspectionFinding = (
  organizationId: string,
  inspectionId: string,
  id: string
) => {
  return useQuery({
    queryKey: [FINDINGS_KEY, organizationId, inspectionId, id],
    queryFn: () =>
      inspectionFindingService
        .findById(organizationId, inspectionId, id)
        .then((res) => res.data),
    enabled: !!organizationId && !!inspectionId && !!id,
  });
};

export const useCreateInspectionFinding = (
  organizationId: string,
  inspectionId: string
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (finding: InspectionsFindingDto) =>
      inspectionFindingService
        .create(organizationId, inspectionId, finding)
        .then((res) => res.data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [FINDINGS_KEY, organizationId, inspectionId],
      });
    },
  });
};

export const useUpdateInspectionFinding = (
  organizationId: string,
  inspectionId: string
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, finding }: { id: string; finding: InspectionsFindingDto }) =>
      inspectionFindingService
        .update(organizationId, inspectionId, id, finding)
        .then((res) => res.data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({
        queryKey: [FINDINGS_KEY, organizationId, inspectionId],
      });
      queryClient.invalidateQueries({
        queryKey: [FINDINGS_KEY, organizationId, inspectionId, id],
      });
    },
  });
};

export const useDeleteInspectionFinding = (
  organizationId: string,
  inspectionId: string
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) =>
      inspectionFindingService
        .delete(organizationId, inspectionId, id)
        .then((res) => res.data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [FINDINGS_KEY, organizationId, inspectionId],
      });
    },
  });
}; 